import { inject, Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse
} from '@angular/common/http';
import { catchError, Observable, throwError, switchMap, take, finalize, timeout, Subject, takeUntil } from 'rxjs';
import { TokenResponseDto } from '../models/auth.models';
import { AuthService } from '../services/auth.service';

/**
 * Auth endpoint suffixes that are part of the auth flow itself.
 * Two behaviours flow from this list:
 *   1. They are excluded from automatic 401 retry/refresh — retrying them
 *      would cause recursive interceptor deadlocks (e.g. refresh-token
 *      getting a 401 triggers another refresh).
 *   2. They are exempt from the logout-cancel signal — the BE revoke call
 *      fired by AuthService.logout() must be allowed to complete even though
 *      logout$ is emitted in the same tick.
 */
const AUTH_ENDPOINT_SUFFIXES: string[] = [
    'api/auth/login',
    'api/auth/logout',
    'api/auth/refresh-token',
    'api/auth/validate-user',
    'api/auth/forgot-password',
    'api/auth/reset-password',
    'api/auth/validate-reset-token',
];

/** Maximum time (ms) queued requests will wait for an in-flight token refresh before giving up. */
const REFRESH_WAIT_TIMEOUT_MS: number = 15_000;

/**
 * Discriminated union pushed to the refresh-result subject so queued requests can
 * distinguish a successful refresh from a failure and react synchronously instead
 * of silently waiting for the timeout.
 */
type RefreshResult =
    | { type: 'success'; token: string }
    | { type: 'failure'; error: unknown };

class PropagatedRefreshFailure extends Error {
    constructor(public readonly original: unknown) {
        super('Propagated refresh failure');
        this.name = 'PropagatedRefreshFailure';
    }
}

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

    private _isTokenRefreshing: boolean = false;
    /**
     * Plain Subject (not BehaviorSubject) — we only care about live notifications;
     * late subscribers should not consume a stale "success" or "failure" from a
     * previous refresh cycle.
     */
    private _refreshResult$: Subject<RefreshResult> = new Subject<RefreshResult>();

    private _authService: AuthService = inject(AuthService);

    public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {

        // Skip modifying JSONP requests (headers are not supported for JSONP)
        if (req.method === 'JSONP') {
            return next.handle(req);
        }

        const token: string | null = localStorage.getItem('accessToken');

        if (token) {
            req = req.clone({
                setHeaders: {
                    'Authorization': 'Bearer ' + token
                }
            });
        }

        // Only set JSON Content-Type when the body is not FormData
        const isFormData: boolean = req.body instanceof FormData;
        /** Blob/arraybuffer downloads (e.g. Files/download) must not send Accept: application/json or the server may return empty/wrong bodies for CSV, etc. */
        const expectsBinaryResponse: boolean =
            req.responseType === 'blob' || req.responseType === 'arraybuffer';

        // Request body JSON: set Content-Type when there is a body (GET/blob downloads have no body).
        if (!isFormData && req.body != null && !req.headers.has('Content-Type')) {
            req = req.clone({
                setHeaders: {
                    'Content-Type': 'application/json'
                }
            });
        }

        if (!isFormData && !expectsBinaryResponse) {
            req = req.clone({
                headers: req.headers.set('Accept', 'application/json')
            });
        }

        const isAuth: boolean = this._isAuthEndpoint(req);

        let stream: Observable<HttpEvent<unknown>> = next.handle(req).pipe(
            catchError((error: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
                if (error.status === 401 && !isAuth) {
                    return this._handle401Error(req, next);
                }

                // Auth endpoints that receive 401 should force logout immediately
                if (error.status === 401 && isAuth) {
                    this._forceLogout();
                    return throwError((): HttpErrorResponse => error);
                }

                return throwError((): HttpErrorResponse => error);
            })
        );

        // Cancel any non-auth request the moment a logout starts. This kills
        // the XHR (Chrome shows it as "(canceled)") so an in-flight request
        // from a soon-to-be-destroyed component cannot complete after the
        // logout, linger in the network panel, or 401 against an empty token.
        // Auth endpoints — including the BE revoke call from logout itself —
        // are exempt so they can complete even though logout$ is emitting.
        if (!isAuth) {
            stream = stream.pipe(takeUntil(this._authService.logout$));
        }

        return stream;
    }

    /**
     * Determines whether the request targets an auth endpoint that should be excluded
     * from automatic 401 refresh handling to prevent recursive interceptor deadlocks.
     */
    private _isAuthEndpoint(request: HttpRequest<unknown>): boolean {
        return AUTH_ENDPOINT_SUFFIXES.some((suffix: string): boolean => request.url.includes(suffix));
    }

    private _handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        // If a logout is already in progress, the user is mid-navigation to
        // /auth/login (or /session-expired). A stray in-flight request that
        // 401s during this window must NOT try to refresh or force-logout,
        // because doing so would navigate to /session-expired and override
        // whatever destination the logout flow was heading to. Just surface
        // the error and let the original request's caller deal with it —
        // the caller is about to be torn down by the navigation anyway.
        if (this._authService.isLoggingOut) {
            return throwError((): Error => new Error('logout in progress'));
        }

        if (!this._isTokenRefreshing) {
            this._isTokenRefreshing = true;

            return this._authService.getNewRefreshToken().pipe(
                switchMap((result: TokenResponseDto): Observable<HttpEvent<unknown>> => {
                    if (result && result.accessToken && result.refreshToken) {
                        localStorage.setItem('accessToken', result.accessToken);
                        localStorage.setItem('refreshToken', result.refreshToken);
                        // Notify queued requests that refresh succeeded with the new token.
                        this._refreshResult$.next({ type: 'success', token: result.accessToken });

                        // Retry the original request with the new token
                        return this._retryRequest(request, next, result.accessToken);
                    } else {
                        const failure: Error = new Error('Token refresh failed');
                        this._refreshResult$.next({ type: 'failure', error: failure });
                        this._forceLogout();
                        return throwError((): Error => failure);
                    }
                }),
                catchError((error: unknown): Observable<never> => {
                    // Notify queued requests that refresh failed so they exit immediately
                    // instead of waiting REFRESH_WAIT_TIMEOUT_MS for the timeout.
                    this._refreshResult$.next({ type: 'failure', error });
                    this._forceLogout();
                    return throwError((): unknown => error);
                }),
                finalize((): void => {
                    // Always reset the flag so the next 401 can trigger a fresh refresh attempt.
                    // Critical: even if getNewRefreshToken errored synchronously, this still runs
                    // because the throwError observable is constructed before pipe().
                    this._isTokenRefreshing = false;
                })
            );
        } else {
            // A refresh is already in-flight: queue this request and wait for the result.
            // Timeout is a safety net; the success/failure notification should arrive first.
            return this._refreshResult$.pipe(
                take(1),
                timeout(REFRESH_WAIT_TIMEOUT_MS),
                switchMap((result: RefreshResult): Observable<HttpEvent<unknown>> => {
                    if (result.type === 'success') {
                        return this._retryRequest(request, next, result.token);
                    }
                    // Refresh failed — _forceLogout was already called by the originating request.
                    // Wrap in a sentinel so the downstream catchError can distinguish this
                    // from a real timeout and skip calling _forceLogout a second time.
                    return throwError((): PropagatedRefreshFailure => new PropagatedRefreshFailure(result.error));
                }),
                catchError((err: unknown): Observable<never> => {
                    // Propagated refresh-failure: originating request already force-logged-out,
                    // just surface the original error.
                    if (err instanceof PropagatedRefreshFailure) {
                        return throwError((): unknown => err.original);
                    }
                    // Real timeout or other unexpected error — the originating request never
                    // signalled. Force logout as a safety measure (this should be very rare).
                    this._forceLogout();
                    return throwError((): unknown => err);
                })
            );
        }
    }

    /**
     * Triggered when a refresh fails or an auth endpoint itself returns 401 —
     * i.e. the session is unrecoverable and the user must sign in again.
     * Delegates to the AuthService which owns the full logout sequence
     * (flag raise, local state wipe, BE revoke, navigation, flag release).
     * This is the ONLY difference from a user-initiated logout: destination
     * is /session-expired so the UI can show a friendly "please sign in again"
     * screen instead of the bare login form.
     */
    private _forceLogout(): void {
        this._authService.logout('/session-expired');
    }

    private _retryRequest(request: HttpRequest<unknown>, next: HttpHandler, token: string): Observable<HttpEvent<unknown>> {
        const authReq: HttpRequest<unknown> = request.clone({
            setHeaders: {
                'Authorization': 'Bearer ' + token
            }
        });

        return next.handle(authReq);
    }
}
